import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
  alpha,
  styled,
  Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import BusinessIcon from '@mui/icons-material/Business';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import axiosInstance from '../../lib/axios';
import { useSnackbar } from '../../components/SnackbarProvider';
import { DeletionImpactDialog } from '../../components/organizations/DeletionImpactDialog';
import { useDeletionImpact, useSoftDeleteOrganization } from '../../hooks/useOrganizationDeletion';

const fetchOrganizations = async (page: number, pageSize: number) => {
  const response = await axiosInstance.get(`/organizations`, {
    params: {
      page: page + 1,
      limit: pageSize,
    },
  });

  return {
    data: Array.isArray(response.data) ? response.data : response.data.data || [],
    total: response.data.total || (Array.isArray(response.data) ? response.data.length : 0),
  };
};

const StyledCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(to bottom right, #ffffff, #fafbfc)',
  borderRadius: '16px',
  boxShadow: '0 4px 24px rgba(0, 0, 0, 0.06)',
  border: '1px solid rgba(0, 0, 0, 0.06)',
  overflow: 'hidden',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
  },
}));

const HeaderBox = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, #0891b2 0%, #6366f1 100%)',
  padding: '48px 40px',
  borderRadius: '16px',
  marginBottom: '32px',
  boxShadow: '0 12px 40px rgba(8, 145, 178, 0.25)',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'radial-gradient(circle at 20% 50%, rgba(255, 255, 255, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(255, 255, 255, 0.08) 0%, transparent 50%)',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: '-50%',
    right: '-10%',
    width: '40%',
    height: '200%',
    background: 'linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.03), transparent)',
    transform: 'rotate(15deg)',
    pointerEvents: 'none',
  },
}));

const SearchField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: '#fafbfc',
    },
    '&.Mui-focused': {
      backgroundColor: '#ffffff',
      boxShadow: '0 0 0 4px rgba(8, 145, 178, 0.12)',
    },
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: '2px solid transparent',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: alpha('#0891b2', 0.3),
  },
  '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#0891b2',
  },
}));

const ActionButton = styled(IconButton)(({ theme }) => ({
  transition: 'all 0.2s ease',
  '&:hover': {
    transform: 'scale(1.1)',
  },
}));

export const OrganizationList = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();
  const [searchQuery, setSearchQuery] = useState('');
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [orgToDelete, setOrgToDelete] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['organizations', paginationModel.page, paginationModel.pageSize],
    queryFn: () => fetchOrganizations(paginationModel.page, paginationModel.pageSize),
  });

  const { data: deletionImpact, isLoading: isLoadingImpact } = useDeletionImpact(
    orgToDelete || '',
    deleteDialogOpen && !!orgToDelete
  );

  const softDeleteMutation = useSoftDeleteOrganization();

  const handleDeleteClick = (id: string) => {
    setOrgToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (orgToDelete) {
      softDeleteMutation.mutate(orgToDelete, {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['organizations'] });
          showMessage('Organization moved to trash successfully', 'success');
          setDeleteDialogOpen(false);
          setOrgToDelete(null);
        },
        onError: (error: any) => {
          showMessage(error.response?.data?.message || 'Failed to delete organization', 'error');
        },
      });
    }
  };

  const handleDeleteDialogClose = () => {
    if (!softDeleteMutation.isPending) {
      setDeleteDialogOpen(false);
      setOrgToDelete(null);
    }
  };

  const filteredData = data?.data?.filter((org: any) =>
    searchQuery
      ? org.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        org.contactEmail?.toLowerCase().includes(searchQuery.toLowerCase())
      : true
  ) || [];

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Organization',
      flex: 1,
      minWidth: 250,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '10px',
              background: 'linear-gradient(135deg, #0891b2 0%, #6366f1 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              fontWeight: 600,
              fontSize: '14px',
              flexShrink: 0,
            }}
          >
            {params.value?.charAt(0)?.toUpperCase() || 'O'}
          </Box>
          <Box>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: '#1a1f36',
                fontSize: '14px',
              }}
            >
              {params.value}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: 'contactEmail',
      headerName: 'Contact Email',
      flex: 1,
      minWidth: 220,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{
            color: '#697386',
            fontSize: '13px',
          }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'contactPhone',
      headerName: 'Phone',
      width: 160,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{
            color: '#697386',
            fontSize: '13px',
            fontFamily: 'monospace',
          }}
        >
          {params.value || '—'}
        </Typography>
      ),
    },
    {
      field: 'address',
      headerName: 'Location',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{
            color: '#697386',
            fontSize: '13px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {params.value || '—'}
        </Typography>
      ),
    },
    {
      field: 'subscriptionPlan',
      headerName: 'Plan',
      width: 150,
      renderCell: (params) => (
        <Chip
          label={params.row.subscriptionPlan || '—'}
          size="small"
          sx={{ textTransform: 'capitalize' }}
        />
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.row.isActive ? 'Active' : 'Inactive'}
          color={params.row.isActive ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 160,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <ActionButton
            size="small"
            onClick={() => navigate(`/organizations/show/${params.row.id}`)}
            sx={{
              color: '#0891b2',
              '&:hover': {
                backgroundColor: alpha('#0891b2', 0.1),
              },
            }}
          >
            <VisibilityIcon fontSize="small" />
          </ActionButton>
          <ActionButton
            size="small"
            onClick={() => navigate(`/organizations/edit/${params.row.id}`)}
            sx={{
              color: '#4CAF50',
              '&:hover': {
                backgroundColor: alpha('#4CAF50', 0.1),
              },
            }}
          >
            <EditIcon fontSize="small" />
          </ActionButton>
          <ActionButton
            size="small"
            onClick={() => handleDeleteClick(params.row.id)}
            sx={{
              color: '#f44336',
              '&:hover': {
                backgroundColor: alpha('#f44336', 0.1),
              },
            }}
          >
            <DeleteIcon fontSize="small" />
          </ActionButton>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ pb: 4 }}>
      <HeaderBox>
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <BusinessIcon sx={{ fontSize: 40, color: '#fff', opacity: 0.9 }} />
            <Typography
              variant="h3"
              sx={{
                color: '#fff',
                fontWeight: 700,
                fontSize: '36px',
                letterSpacing: '-0.02em',
              }}
            >
              Organizations
            </Typography>
          </Box>
          <Typography
            variant="body1"
            sx={{
              color: 'rgba(255, 255, 255, 0.85)',
              fontSize: '15px',
              maxWidth: '600px',
            }}
          >
            Manage and oversee all organizations, subscriptions, and enterprise accounts
          </Typography>
        </Box>
      </HeaderBox>

      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
        <SearchField
          placeholder="Search organizations..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          sx={{ flex: 1, maxWidth: 400 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#697386' }} />
              </InputAdornment>
            ),
          }}
        />
        <Box sx={{ flex: 1 }} />
        <Button
          variant="outlined"
          startIcon={<DeleteSweepIcon />}
          onClick={() => navigate('/organizations/trash')}
          sx={{
            borderRadius: '10px',
            padding: '10px 24px',
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '14px',
            borderColor: '#697386',
            color: '#697386',
            mr: 2,
            '&:hover': {
              borderColor: '#f44336',
              color: '#f44336',
              backgroundColor: alpha('#f44336', 0.05),
            },
          }}
        >
          Trash
        </Button>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/organizations/create')}
          sx={{
            background: 'linear-gradient(135deg, #0891b2 0%, #6366f1 100%)',
            borderRadius: '10px',
            padding: '10px 24px',
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '14px',
            boxShadow: '0 4px 14px 0 rgba(8, 145, 178, 0.35)',
            transition: 'all 0.3s ease',
            '&:hover': {
              background: 'linear-gradient(135deg, #0e7490 0%, #4f46e5 100%)',
              boxShadow: '0 6px 20px 0 rgba(8, 145, 178, 0.45)',
              transform: 'translateY(-2px)',
            },
          }}
        >
          Create Organization
        </Button>
      </Box>

      <StyledCard>
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          <DataGrid
            rows={filteredData}
            columns={columns}
            rowCount={data?.total || 0}
            loading={isLoading}
            pageSizeOptions={[5, 10, 25, 50]}
            paginationModel={paginationModel}
            paginationMode="server"
            onPaginationModelChange={setPaginationModel}
            autoHeight
            disableRowSelectionOnClick
            disableVirtualization={process.env.NODE_ENV === 'test'}
            sx={{
              border: 'none',
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#fafbfc',
                borderBottom: '2px solid #e3e8ef',
                borderRadius: 0,
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                fontWeight: 700,
                fontSize: '12px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                color: '#697386',
              },
              '& .MuiDataGrid-row': {
                borderBottom: '1px solid #e3e8ef',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: alpha('#0891b2', 0.03),
                  cursor: 'pointer',
                },
              },
              '& .MuiDataGrid-cell': {
                borderBottom: 'none',
                padding: '16px',
              },
              '& .MuiDataGrid-footerContainer': {
                borderTop: '2px solid #e3e8ef',
                backgroundColor: '#fafbfc',
              },
            }}
          />
        </CardContent>
      </StyledCard>

      <DeletionImpactDialog
        open={deleteDialogOpen}
        onClose={handleDeleteDialogClose}
        impact={deletionImpact}
        isLoading={isLoadingImpact}
        onConfirm={handleDeleteConfirm}
        isDeleting={softDeleteMutation.isPending}
      />
    </Box>
  );
};
